import {
  INFLATE_RAMP_START_FRACTION,
  LOW_ZOOM_INFLATE_PX,
  ZOOM_QUANTUM_DIVISOR,
} from "../constants";
import type { BboxGeometry, ComputeBboxGeometry } from "./types";

/** Snap a low-zoom value to a discrete bucket so inflatePdf only changes at coarse steps. */
const snapToBucket = (zoomLevel: number, opacityThreshold: number): number => {
  const bucketSize = opacityThreshold / ZOOM_QUANTUM_DIVISOR;
  return Math.max(bucketSize, Math.round(zoomLevel / bucketSize) * bucketSize);
};

/** Linear ramp from LOW_ZOOM_INFLATE_PX down to the stroke-half value across the band approaching threshold. */
const rampedInflatePx = (
  zoomLevel: number,
  opacityThreshold: number,
  strokeWidth: number,
): number => {
  const rampStartZoom = opacityThreshold * INFLATE_RAMP_START_FRACTION;
  if (zoomLevel <= rampStartZoom) return LOW_ZOOM_INFLATE_PX;

  // Stroke is in coord units and scales with zoom, so its screen-pixel
  // contribution per side at the threshold is `(strokeWidth * threshold) / 2`.
  const strokeHalfAtThreshold = (strokeWidth * opacityThreshold) / 2;
  const rampBandWidth = opacityThreshold - rampStartZoom;
  const zoomIntoRampBand = zoomLevel - rampStartZoom;
  const rampProgress = zoomIntoRampBand / rampBandWidth;

  return (
    LOW_ZOOM_INFLATE_PX +
    (strokeHalfAtThreshold - LOW_ZOOM_INFLATE_PX) * rampProgress
  );
};

export const computeBboxGeometry: ComputeBboxGeometry = ({
  zoomLevel,
  opacityThreshold,
  strokeWidth,
}): BboxGeometry => {
  const isLowZoom = zoomLevel < opacityThreshold;

  if (!isLowZoom) {
    return { isLowZoom, inflatePdf: 0 };
  }

  const bucketedZoom = snapToBucket(zoomLevel, opacityThreshold);
  const inflatePx = rampedInflatePx(
    bucketedZoom,
    opacityThreshold,
    strokeWidth,
  );
  const inflatePdf = inflatePx > 0 ? inflatePx / bucketedZoom : 0;

  return { isLowZoom, inflatePdf };
};
